import { inject, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, tap } from "rxjs";
import { Router } from "@angular/router";
import { LoginResponse } from "@core/models/loginResponse";
import { environment } from "@env/environment";
import { User } from "@core/models/user";
import { SettingsService } from "./settings.service";
@Injectable({
  providedIn: "root",
})
export class AuthService {
  private apiUrl = environment.apiUrl;
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  private http = inject(HttpClient);
  private router = inject(Router);
  private settingsService = inject(SettingsService);

  constructor() {
    this.loadUserFromStorage();
  }

  // Connexion
  login(usernameOrEmail: string, password: string): Observable<LoginResponse> {
    return this.http
        .post<LoginResponse>(`${this.apiUrl}/auth/login`, {
          usernameOrEmail,
          password,
        })
        .pipe(
            tap((response) => {
              console.debug("[AuthService] login response", response);
              if (response.userDTO) {
                const rolesRaw = (response.userDTO as unknown as { roles?: unknown })?.roles;
                let roles: string[] = [];
                if (Array.isArray(rolesRaw)) {
                  roles = (rolesRaw as unknown[]).map((r) => String(r).trim().toUpperCase().replace(/^ROLE_/, ""));
                } else if (typeof rolesRaw === "string") {
                  roles = (rolesRaw as string)
                      .split(",")
                      .map((s) => s.trim().toUpperCase().replace(/^ROLE_/, ""))
                      .filter((s) => !!s);
                }
                (response.userDTO as any).roles = roles;

                const permsRaw = (response.userDTO as unknown as { permissions?: unknown })?.permissions;
                let permissions: string[] = [];
                if (Array.isArray(permsRaw)) {
                  permissions = (permsRaw as unknown[]).map((p) => String(p).trim().toUpperCase());
                }
                (response.userDTO as any).permissions = permissions;
              }

              this.setSession(response);

              if (response.userDTO) {
                this.currentUserSubject.next(response.userDTO as unknown as User);

                const options = this.settingsService.getOptions();
                options.navPos = "top";
                options.color = "purple";
                options.showHeader = true;
                options.headerPos = "fixed";
                this.settingsService.setOptions(options);
              }
            })
        );
  }

  register(userData: {
    email: string;
    password: string;
    name: string;
  }): Observable<LoginResponse> {
    return this.http
        .post<LoginResponse>(`${this.apiUrl}/auth/register`, userData)
        .pipe(
            tap((response) => {
              this.setSession(response);
            })
        );
  }

  logout(): void {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    this.currentUserSubject.next(null);
    this.router.navigate(["/auth/login"]);
  }

  private setSession(authResult: LoginResponse): void {
    localStorage.setItem("token", authResult.accessToken || "");
    if (authResult.userDTO)
      localStorage.setItem("user", JSON.stringify(authResult.userDTO));
  }

  private loadUserFromStorage(): void {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const parsed = JSON.parse(userStr);
        const rawRoles = (parsed as { roles?: unknown }).roles;
        let roles: string[] = [];
        if (Array.isArray(rawRoles)) {
          roles = (rawRoles as unknown[]).map((r) => String(r).trim().toUpperCase().replace(/^ROLE_/, ""));
        } else if (typeof rawRoles === "string") {
          roles = (rawRoles as string)
              .split(",")
              .map((s) => s.trim().toUpperCase().replace(/^ROLE_/, ""))
              .filter((s) => !!s);
        }
        (parsed as any).roles = roles;

        const rawPerms = (parsed as { permissions?: unknown }).permissions;
        let permissions: string[] = [];
        if (Array.isArray(rawPerms)) {
          permissions = (rawPerms as unknown[]).map((p) => String(p).trim().toUpperCase());
        }
        (parsed as any).permissions = permissions;

        this.currentUserSubject.next(parsed);
      } catch {
        this.currentUserSubject.next(JSON.parse(userStr));
      }
    }
  }

  /**
   * Returns true if the current user has the given permission name (case-insensitive).
   */
  /**
   * Returns true if the current user is in an appart-hôtel context.
   *
   * Priority order:
   * 1. appartHotelId set in token (reliable, after backend restart + re-login)
   * 2. Explicit appart-hôtel roles (works for any session)
   * 3. Permission heuristic: has apartment access but no hotel management access
   *    → covers ANY role (RECEPTIONNISTE, MANAGER, COMPTABLE…) created by ADMIN_APPART_HOTEL
   */
  isAppartHotelContext(): boolean {
    const user = this.getCurrentUser();
    const roles = user?.roles ?? [];
    return !!(user?.appartHotelId)
      || roles.includes('ADMIN_APPART_HOTEL')
      || roles.includes('CHARGE_OPERATIONS')
      || roles.includes('MANAGER')
      || (this.can('RECUPERER_LISTE_APPARTEMENTS') && !this.can('RECUPERER_LISTE_HOTELS'));
  }

  can(permission: string): boolean {
    const user = this.getCurrentUser();
    if (!user?.permissions) return false;
    return user.permissions.includes(permission.toUpperCase());
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem("token");
  }

  /**
   * Vrai si le token JWT courant est expiré (claim `exp`, en secondes).
   * - Absence de token → considéré expiré.
   * - Token présent mais sans claim `exp` exploitable → non expiré (on laisse le
   *   serveur trancher via un 401), pour éviter une déconnexion à tort.
   */
  isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) return true;
    const claims = this.decodeJwt(token);
    if (!claims || typeof claims.exp !== "number") return false;
    return claims.exp * 1000 <= Date.now();
  }

  getToken(): string | null {
    return localStorage.getItem("token");
  }

  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  // Decode a JWT and return payload (no external deps)
  private decodeJwt(token: string): any | null {
    try {
      const payload = token.split('.')[1];
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const json = decodeURIComponent(
          atob(base64)
              .split('')
              .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join('')
      );
      return JSON.parse(json);
    } catch {
      return null;
    }
  }

  hasToChangePassword(): boolean {
    const token = this.getToken();
    if (!token) return false;
    const claims = this.decodeJwt(token);
    return claims?.mustChangePassword === true;
  }

  changePassword(oldPassword: string, newPassword: string) {
    return this.http
        .put<void>(`${this.apiUrl}/auth/me/change-password`, {
          oldPassword,
          newPassword,
        })
        .pipe(
            tap(() => {
              this.logout();
            })
        );
  }

  requestPasswordReset(email: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/auth/forgot-password`, { email });
  }

  resetPassword(token: string, newPassword: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/auth/reset-password`, { token, newPassword });
  }
}
